import React, { useState, useEffect, useRef, useCallback } from 'react';
import { rtdb } from '../firebase'; 
import { ref, query, orderByChild, equalTo, onValue, update, get, endAt } from 'firebase/database';
import './AdminNotification.css';

const AdminNotification = ({ onNavigateToProgress, targetFg }) => { 
  const [currentView, setCurrentView] = useState('Pending'); 
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedDates, setCollapsedDates] = useState({});
  
  const previousAlertIdsRef = useRef(new Set());
  const isInitialLoad = useRef(true);
  
  const targetCardRef = useRef(null);

  // Request Desktop Notification Permission on Mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
        Notification.requestPermission();
      }
    }
  }, []);

  // 🛠 Helper Function wrapped in useCallback to stabilize its identity across renders
  const triggerDesktopNotification = useCallback((alertItem) => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      const title = alertItem.type || "New System Anomaly";
      const options = {
        body: `Station: ${alertItem.station || 'Unknown'}\nFG Number: ${alertItem.fgNumber || 'N/A'}\nDetected By: ${alertItem.detectedBy || 'System'}`,
        icon: '/favicon.ico',
        requireInteraction: false 
      };

      const notification = new Notification(title, options);

      notification.onclick = (e) => {
        e.preventDefault();
        window.focus();
        if (alertItem.fgNumber && onNavigateToProgress) {
          onNavigateToProgress(alertItem.fgNumber);
        }
      };
    }
  }, [onNavigateToProgress]);

  // Sync Live Data Streams & Manage Automated System Alerts
  useEffect(() => {
    setLoading(true);
    cleanOldAlerts();

    const alertsRef = ref(rtdb, 'Alerts');
    const statusQuery = query(alertsRef, orderByChild('status'), equalTo(currentView));

    const unsubscribe = onValue(statusQuery, (snapshot) => {
      const rawAlerts = [];
      const currentSnapshotIds = new Set();

      snapshot.forEach((childSnapshot) => {
        const id = childSnapshot.key;
        currentSnapshotIds.add(id);
        rawAlerts.push({
          id,
          ...childSnapshot.val()
        });
      });

      rawAlerts.reverse();

      // Desktop notification firing logic
      if (currentView === 'Pending' && !isInitialLoad.current) {
        rawAlerts.forEach((newAlert) => {
          if (!previousAlertIdsRef.current.has(newAlert.id)) {
            triggerDesktopNotification(newAlert);
          }
        });
      }

      previousAlertIdsRef.current = currentSnapshotIds;

      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }

      setAlerts(rawAlerts);

      // --- PERSISTENT AUTO-COLLAPSE LOGIC ---
      const validAlerts = rawAlerts.filter(item => item.dateTime && item.dateTime.trim() !== "");
      setCollapsedDates((prevCollapsedStates) => {
        const updatedCollapseState = { ...prevCollapsedStates };
        validAlerts.forEach((alertItem) => {
          const parts = alertItem.dateTime.trim().split(" ");
          const dateStr = parts[0] || "No Date";
          
          if (targetFg && alertItem.fgNumber === targetFg) {
            updatedCollapseState[dateStr] = false; 
          } else if (updatedCollapseState[dateStr] === undefined) {
            updatedCollapseState[dateStr] = true;
          }
        });
        return updatedCollapseState;
      });

      setLoading(false);
    }, (error) => {
      console.error("Firebase Database Sync Error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentView, targetFg, triggerDesktopNotification]); // ✅ Added triggerDesktopNotification safely here

  // AUTO-SCROLL LIFECYCLE EFFECT
  useEffect(() => {
    if (!loading && targetFg && targetCardRef.current) {
      setTimeout(() => {
        targetCardRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300); 
    }
  }, [loading, targetFg]);

  // Reset collapse states when switching views manually
  useEffect(() => {
    setCollapsedDates({});
    isInitialLoad.current = true; 
  }, [currentView]);

  // BANDWIDTH FIX: Download exclusively expired nodes matching constraints
  const cleanOldAlerts = async () => {
    const thirtyDaysInMillis = 30 * 24 * 60 * 60 * 1000;
    const expirationThreshold = Date.now() - thirtyDaysInMillis;
    const alertsRef = ref(rtdb, 'Alerts');

    try {
      const expiredQuery = query(alertsRef, orderByChild('timestamp'), endAt(expirationThreshold));
      const snapshot = await get(expiredQuery);
      const updates = {};
      
      snapshot.forEach((child) => {
        updates[`Alerts/${child.key}`] = null;
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(rtdb), updates);
        console.log(`Successfully cleared ${Object.keys(updates).length} expired alert entries.`);
      }
    } catch (err) {
      console.error("Auto-cleanup lifecycle failed:", err);
    }
  };

  const toggleAlertStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === 'Pending' ? 'Completed' : 'Pending';
    try {
      await update(ref(rtdb, `Alerts/${id}`), { status: nextStatus });
    } catch (err) {
      alert("Failed to update status record properties.");
    }
  };

  const toggleDateGroup = (date) => {
    setCollapsedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const getSolutionMessage = (type, fgNumber) => {
    const fallbackFg = fgNumber || "N/A";
    switch (type) {
      case "FG Not Recorded at Receive Station":
        return `Scan FG No: ${fallbackFg} at Receive Station`;
      case "FG Not Recorded at Treat Station":
        return `Scan FG No: ${fallbackFg} at Treat Station`;
      case "FG Not Recorded at Paint Station":
        return `Scan FG No: ${fallbackFg} at Paint Station`;
      case "FG Not Recorded at Pack Station":
        return `Scan FG No: ${fallbackFg} at Pack Station`;
      case "FG Not Recorded at Deliver Station":
      default:
        return "System operation anomaly detected.";
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return "--:--";
    try {
      const parsedTime = new Date(dateString.replace(/-/g, "/")).getTime();
      if (isNaN(parsedTime)) return dateString;

      const diff = Date.now() - parsedTime;
      const minutes = Math.floor(diff / 60000);
      
      if (minutes < 1) return "Just now";
      if (minutes < 60) return `${minutes}m ago`;
      
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    } catch (e) {
      return dateString || "";
    }
  };

  const validAlerts = alerts.filter(item => item.dateTime && item.dateTime.trim() !== "");
  const groupedMap = validAlerts.reduce((acc, alertItem) => {
    const parts = alertItem.dateTime.trim().split(" ");
    const dateStr = parts[0] || "No Date";
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(alertItem);
    return acc;
  }, {});

  return (
    <div className="admin-notification-container">
      <header className="common-header">
        <div className="header-title">Notification Hub</div>
      </header>

      <main className="notification-workspace">
        <div className="dashboard-controls">
          <div className="title-area">
            <h1 className="dashboard-title">
              {currentView === 'Pending' ? 'Active Alerts' : 'Archived Alerts'}
            </h1>
            <span className="badge-count">
              {alerts.length} {alerts.length === 1 ? 'issue' : 'issues'}
            </span>
          </div>
          
          <div className="segmented-control">
            <button 
              onClick={() => setCurrentView('Pending')}
              className={`control-btn ${currentView === 'Pending' ? 'active alert-active' : ''}`}
            >
              Pending
            </button>
            <button 
              onClick={() => setCurrentView('Completed')}
              className={`control-btn ${currentView === 'Completed' ? 'active archive-active' : ''}`}
            >
              Completed
            </button>
          </div>
        </div>

        {currentView === 'Completed' && (
          <div className="cleanup-notice">
            <span className="sparkle">✦</span> Records automatically purged after 30 days of storage <span className="sparkle">✦</span>
          </div>
        )}

        <div className="alerts-list">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <div className="state-message">Syncing live data streams...</div>
            </div>
          ) : Object.keys(groupedMap).length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <div className="state-message empty">All systems operational</div>
              <p className="empty-subtext">No anomalies recorded under this filter.</p>
            </div>
          ) : (
            Object.entries(groupedMap).map(([date, items]) => {
              const isCollapsed = !!collapsedDates[date];
              return (
                <div key={date} className={`date-group ${isCollapsed ? 'is-collapsed' : ''}`}>
                  <div 
                    className="date-header" 
                    onClick={() => toggleDateGroup(date)}
                  >
                    <div className="date-header-left">
                      <span className="calendar-icon">📅</span> 
                      <span className="date-text">{date}</span>
                      <span className="date-count-badge">{items.length}</span>
                    </div>
                    <div className="date-header-right">
                      <svg className="collapse-arrow-svg" viewBox="0 0 24 24" width="25" height="25">
                        <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="cards-stack-wrapper">
                    <div className="cards-stack">
                      {items.map((alertItem) => {
                        const isTargeted = targetFg && String(alertItem.fgNumber) === String(targetFg);

                        return (
                          <div 
                            key={alertItem.id} 
                            ref={isTargeted ? targetCardRef : null} 
                            className={`alert-card status-${alertItem.status.toLowerCase()} ${isTargeted ? 'highlight-flash-card' : ''}`} 
                          >
                            <div 
                              className="card-info"
                              onClick={() => onNavigateToProgress && onNavigateToProgress(alertItem.fgNumber)}
                              title="Click to track progress"
                            >
                              <div className="card-top-row">
                                <span className="alert-type">{alertItem.type || "Unknown Anomaly"}</span>
                                <span className="fg-tag">FG: {alertItem.fgNumber || "N/A"}</span>
                              </div>
                              
                              <div className="solution-box">
                                <span className="lightbulb">💡</span>
                                <p className="solution-text">
                                  {getSolutionMessage(alertItem.type, alertItem.fgNumber)}
                                </p>
                              </div>

                              <div className="card-footer-metrics">
                                <span className="metric-tag"><span className="geo-icon">📍</span> {alertItem.station || "Global Station"}</span>
                                <span className="metric-tag">👤 {alertItem.detectedBy || "System Core"}</span>
                                <span className="relative-time">{getRelativeTime(alertItem.dateTime)}</span>
                              </div>
                            </div>

                            <div className="card-action-column">
                              <button
                                onClick={() => toggleAlertStatus(alertItem.id, alertItem.status)}
                                className={`action-btn run-${currentView.toLowerCase()}`}
                              >
                                {currentView === 'Pending' ? 'Resolve' : 'Reopen'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminNotification;